// src/auth/authStrategy.ts
//
// CRM's runtime authentication model, as TWO independent dimensions.
//
// ── Why this file exists ─────────────────────────────────────────────────
//
// `baseApi` carried a local predicate:
//
//     function isCentralMode(state) {
//       if (AUTH_MODE === 'legacy') return false;
//       return Boolean(state.centralAuth?.accessToken);   // ← token presence
//     }
//
// It conflated two different questions into one boolean, and answered both
// from browser token presence. A canonical RP/BFF browser holds NO Central
// token by design — identity lives server-side behind `crm_sid` — so the
// predicate reported `false` for exactly the architecture it was meant to
// describe. Every recovery branch gated on it was therefore unreachable in
// canonical mode, which is why an expired RP session produced an endless 401
// poll with no redirect and no recovery.
//
// The repository already learned this lesson once: `lib/auth/isCentralMode.ts`
// documents three prior PRs (#23, #24, #26) that each fixed a bug caused by
// inferring RP mode from browser-side signals, and states that every call site
// MUST import the canonical predicate. `baseApi`'s copy was an inline duplicate
// the source-level guard never covered, because that guard checks a hard-coded
// list of files rather than scanning the tree.
//
// ── The model ────────────────────────────────────────────────────────────
//
//   AUTH STRATEGY   which authentication owns this browser, by DEPLOYMENT
//                   configuration. Never inferred from tokens.
//
//   SESSION STATE   whether that strategy currently holds a live session.
//                   Answered by the SERVER contract, never by tokens.
//
// They are orthogonal. `canonical_rp` + `unauthenticated` is a perfectly
// ordinary state — it is precisely the live defect — and it is unrepresentable
// in a single token-presence boolean.

import { AUTH_MODE } from '../config/centralAuth';

/** Which authentication strategy owns this browser. */
export type AuthStrategy = 'canonical_rp' | 'legacy_browser';

/** Whether the owning strategy currently holds a usable session. */
export type SessionState = 'authenticated' | 'unauthenticated' | 'unavailable';

/**
 * The strategy this deployment runs under.
 *
 * Derived from deployment configuration ONLY. A canonical RP browser holds no
 * Central token, so token presence can never be an input here without
 * reintroducing the defect.
 */
export function selectAuthStrategy(authMode: string = AUTH_MODE): AuthStrategy {
  return authMode === 'legacy' ? 'legacy_browser' : 'canonical_rp';
}

/** True when the canonical server-session architecture owns this browser. */
export function isCanonicalRpStrategy(authMode: string = AUTH_MODE): boolean {
  return selectAuthStrategy(authMode) === 'canonical_rp';
}

/**
 * Map a server-contract response status onto session state.
 *
 * `/auth/context` is CRM's canonical server-session contract: 200 when the RP
 * session is live, 401 when it is missing, expired or revoked. A 5xx or a
 * transport failure is NOT "signed out" — treating an upstream outage as a
 * logout would bounce users through sign-in during an incident they cannot
 * fix, so it maps to `unavailable` and is handled separately.
 */
export function sessionStateFromContextStatus(status: number | null | undefined): SessionState {
  if (status === 200) return 'authenticated';
  if (status === 401 || status === 403) return 'unauthenticated';
  return 'unavailable';
}

/**
 * Does a 401 from THIS request indicate a lost authenticated session?
 *
 * Not every 401 should start a sign-in. Auth-plumbing and deliberately
 * unauthenticated probes answer 401 as normal operation, and treating those as
 * session loss is how a recovery loop forms. Only authenticated product and
 * context requests qualify.
 */
export function indicatesLostSession(url: string): boolean {
  if (!url) return false;
  // Auth plumbing: /auth/start, /auth/callback, /auth/logout answer for
  // themselves and must never trigger recovery.
  if (/^\/auth\/(start|callback|logout)/.test(url)) return false;
  return true;
}
